<template>
  <WizardModal
    :show-modal="visible"
    :modal-title="t('Conciliação de Adiantamentos')"
    :steps="steps"
    size="xlarge"
    :loading="isProcessing"
    :show-summary="true"
    :summary-items="summaryItems"
    @close="handleClose"
    @finish="handleFinish"
    @cancel="handleClose"
    ref="wizardRef"
  >
    <!-- Step 0: Current Advances Status -->
    <template #step-0>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Status dos Adiantamentos') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise os adiantamentos já marcados para este período de folha') }}
          </p>
        </div>

        <div v-if="isLoadingAdvances" class="loading-container">
          <spinner data-testid="payroll-cash-advances-loading-status" />
        </div>

        <div v-else>
          <!-- KPI Cards -->
          <div class="kpi-grid">
            <div class="kpi-card">
              <div class="kpi-icon info">
                <i class="pi pi-wallet"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-cash-advances-count">{{ currentAdvances.length }}</span>
                <span class="kpi-label">{{ t('Adiantamentos Selecionados') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon success">
                <i class="pi pi-dollar"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-cash-advances-total-amount">{{ n(currentAdvancesTotal, 'currency') }}</span>
                <span class="kpi-label">{{ t('Total de Adiantamentos') }}</span>
              </div>
            </div>

            <div class="kpi-card">
              <div class="kpi-icon warning">
                <i class="pi pi-users"></i>
              </div>
              <div class="kpi-content">
                <span class="kpi-value" data-testid="payroll-cash-advances-unique-users">{{ uniqueUsersWithAdvances }}</span>
                <span class="kpi-label">{{ t('Operadores com Adiantamentos') }}</span>
              </div>
            </div>
          </div>

          <!-- Current Advances Table -->
          <div v-if="currentAdvances.length > 0" class="current-advances-section">
            <h5 class="section-title">{{ t('Adiantamentos Atuais') }}</h5>
            <DataTable
              :config="currentAdvancesTableConfig"
              :data="currentAdvances"
              @button-click="handleCurrentAdvancesButtonClick"
              data-testid="payroll-cash-advances-current-table"
            />
          </div>

          <div v-else class="empty-state">
            <i class="pi pi-wallet"></i>
            <h4>{{ t('Nenhum adiantamento selecionado') }}</h4>
            <p>{{ t('Avance para o próximo passo para selecionar recebimentos em dinheiro como adiantamentos.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 1: Select Available Recebimentos -->
    <template #step-1>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Selecionar Recebimentos em Dinheiro') }}</h4>
          <p class="step-subtitle">
            {{ t('Selecione os recebimentos em dinheiro que foram mantidos pelos operadores') }}
          </p>
        </div>

        <inline-message severity="info" :closable="false" class="info-message">
          <i class="pi pi-info-circle"></i>
          {{ t('Recebimentos em dinheiro marcados como adiantamento serão descontados do valor a pagar ao colaborador.') }}
        </inline-message>

        <div v-if="isLoadingRecebimentos" class="loading-container">
          <spinner data-testid="payroll-cash-recebimentos-loading" />
        </div>

        <div v-else>
          <!-- Filters -->
          <div class="filters-row">
            <select-list
              v-model="selectedOperadorFilter"
              :itens-select="operadorFilterItems"
              :placeholder="t('Todos Operadores')"
              :show-clear="true"
              class="filter-dropdown"
              elem-id="payroll-cash-operador-filter"
              data-testid="payroll-cash-operador-filter"
            />
          </div>

          <!-- Available Recebimentos Table -->
          <div v-if="filteredAvailableRecebimentos.length > 0">
            <DataTable
              v-model:selection="selectedRecebimentos"
              :config="availableRecebimentosTableConfig"
              :data="filteredAvailableRecebimentos"
              data-testid="payroll-cash-recebimentos-available-table"
            />

            <!-- Selection Summary -->
            <div class="selection-summary">
              <div class="selection-info">
                <span v-if="selectedRecebimentos.length > 0">
                  {{ t('Selecionados') }}: <strong>{{ selectedRecebimentos.length }}</strong>
                  ({{ n(selectedTotal, 'currency') }})
                </span>
                <span v-else class="no-selection">
                  {{ t('Selecione recebimentos para adicionar como adiantamentos') }}
                </span>
              </div>
            </div>
          </div>

          <div v-else class="empty-state">
            <i class="pi pi-inbox"></i>
            <h4>{{ t('Nenhum recebimento em dinheiro disponível') }}</h4>
            <p>{{ t('Não há recebimentos em dinheiro no período que possam ser marcados como adiantamento.') }}</p>
          </div>
        </div>
      </div>
    </template>

    <!-- Step 2: Confirm Selection -->
    <template #step-2>
      <div class="wizard-step-content">
        <div class="step-header">
          <h4>{{ t('Confirmar Seleção') }}</h4>
          <p class="step-subtitle">
            {{ t('Revise e confirme os adiantamentos selecionados') }}
          </p>
        </div>

        <!-- Final Summary -->
        <div class="final-summary">
          <div class="summary-card">
            <div class="summary-header">
              <i class="pi pi-check-circle"></i>
              <h5>{{ t('Resumo da Seleção') }}</h5>
            </div>
            <div class="summary-body">
              <div class="summary-row">
                <span class="label">{{ t('Adiantamentos existentes') }}:</span>
                <span class="value">{{ currentAdvances.length }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Novos adiantamentos a adicionar') }}:</span>
                <span class="value highlight">{{ selectedRecebimentos.length }}</span>
              </div>
              <div class="summary-row">
                <span class="label">{{ t('Valor dos novos adiantamentos') }}:</span>
                <span class="value highlight">{{ n(selectedTotal, 'currency') }}</span>
              </div>
              <div class="summary-row total">
                <span class="label">{{ t('Total após confirmação') }}:</span>
                <span class="value">{{ n(currentAdvancesTotal + selectedTotal, 'currency') }}</span>
              </div>
            </div>
          </div>

          <!-- Selected Items Preview -->
          <div v-if="selectedRecebimentos.length > 0" class="selected-preview">
            <h5 class="section-title">{{ t('Recebimentos a Adicionar') }}</h5>
            <DataTable
              :config="selectedPreviewTableConfig"
              :data="selectedRecebimentos"
              data-testid="payroll-cash-selected-preview-table"
            />
          </div>

          <inline-message v-else severity="warn" :closable="false">
            {{ t('Nenhum novo adiantamento selecionado. Clique em "Voltar" para selecionar recebimentos ou "Finalizar" para manter os adiantamentos atuais.') }}
          </inline-message>
        </div>
      </div>
    </template>
  </WizardModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardModal from '@/ui/WizardModal.vue';
import { usePayrollAdvances } from '@/composables/usePayrollAdvances';
import { useDateFormatter } from '@/composables/useDateFormatter';
import { eventBus } from '@/eventBus';
import { log } from '@/utils/DebugLogger';
import { permissionsService } from '@/services/permissionsService';

type Id = number | string;

interface CurrentAdvanceRow {
  id: Id;
  recebimento_data?: string | null;
  cliente_nome?: string;
  user_id?: Id;
  amount?: number | string;
  [key: string]: unknown;
}

interface AvailableRecebimentoRow {
  recebimento_id: Id;
  recebimento_data?: string | null;
  cliente_nome?: string;
  operador_user_id?: Id;
  forma_pagamento_nome?: string;
  amount?: number | string;
  [key: string]: unknown;
}

interface SummaryByUser {
  user_id?: Id;
  total?: number;
  count?: number;
  [key: string]: unknown;
}

interface AdvancesApiResponse {
  advances?: CurrentAdvanceRow[];
  summary_by_user?: SummaryByUser[];
}

interface OperadorFilterOption {
  ID: Id;
  Name: string;
}

interface ButtonClickPayload {
  action: string;
  data: CurrentAdvanceRow;
  value?: unknown;
}

interface CompletedPayload {
  added: number;
}

interface Props {
  visible?: boolean;
  periodId: number;
  franchiseId: number;
  usersMap?: Record<string, { name?: string; user_name?: string } | undefined>;
}

const props = withDefaults(defineProps<Props>(), {
  visible: false,
  usersMap: () => ({}),
});

const emit = defineEmits<{
  'update:visible': [value: boolean];
  'completed': [payload: CompletedPayload];
  'closed': [];
}>();

const { t, n } = useI18n();
const {
  fetchAvailableRecebimentos,
  listAdvancesByPeriod,
  addAdvancesBatch,
  deleteAdvance
} = usePayrollAdvances();
const { formatDate: formatDateLocale } = useDateFormatter();

// Refs
const wizardRef = ref<unknown>(null);
const isLoadingAdvances = ref<boolean>(false);
const isLoadingRecebimentos = ref<boolean>(false);
const isProcessing = ref<boolean>(false);
const deletingId = ref<Id | null>(null);

// Data
const currentAdvances = ref<CurrentAdvanceRow[]>([]);
const summaryByUser = ref<SummaryByUser[]>([]);
const availableRecebimentos = ref<AvailableRecebimentoRow[]>([]);
const selectedRecebimentos = ref<AvailableRecebimentoRow[]>([]);
const selectedOperadorFilter = ref<Id | null>(null);

// Wizard steps configuration
const steps = computed(() => [
  {
    title: t('Status'),
    description: t('Adiantamentos atuais'),
    icon: 'pi pi-chart-bar',
    validate: () => true
  },
  {
    title: t('Selecionar'),
    description: t('Recebimentos disponíveis'),
    icon: 'pi pi-list',
    validate: () => true
  },
  {
    title: t('Confirmar'),
    description: t('Finalizar seleção'),
    icon: 'pi pi-check',
    validate: () => true
  }
]);

// Summary items for wizard header
const summaryItems = computed(() => [
  {
    icon: 'pi pi-calendar',
    label: t('Período'),
    value: `#${props.periodId}`
  },
  {
    icon: 'pi pi-wallet',
    label: t('Adiantamentos'),
    value: `${currentAdvances.value.length} (${n(currentAdvancesTotal.value, 'currency')})`
  },
  {
    icon: 'pi pi-plus-circle',
    label: t('Novos'),
    value: `${selectedRecebimentos.value.length} (${n(selectedTotal.value, 'currency')})`
  }
]);

// Computed: current advances total
const currentAdvancesTotal = computed<number>(() => {
  return currentAdvances.value.reduce((sum, adv) => sum + (parseFloat(String(adv.amount ?? 0)) || 0), 0);
});

// Computed: unique users with advances
const uniqueUsersWithAdvances = computed<number>(() => {
  const users = new Set(currentAdvances.value.map(adv => adv.user_id));
  return users.size;
});

// Computed: list of unique operadores from available recebimentos (as select-list items)
const operadorFilterItems = computed<OperadorFilterOption[]>(() => {
  const operadores = new Map<string, OperadorFilterOption>();
  availableRecebimentos.value.forEach(rec => {
    const opId = rec.operador_user_id;
    if (opId !== undefined && opId !== null) {
      const key = String(opId);
      if (!operadores.has(key)) {
        operadores.set(key, {
          ID: opId,
          Name: getOperadorName(opId)
        });
      }
    }
  });
  return Array.from(operadores.values());
});

// Computed: filtered available recebimentos by operador
const filteredAvailableRecebimentos = computed<AvailableRecebimentoRow[]>(() => {
  if (selectedOperadorFilter.value === null || selectedOperadorFilter.value === undefined) {
    return availableRecebimentos.value;
  }
  return availableRecebimentos.value.filter(
    rec => rec.operador_user_id === selectedOperadorFilter.value
  );
});

// Computed: total of selected recebimentos
const selectedTotal = computed<number>(() => {
  return selectedRecebimentos.value.reduce((sum, rec) => sum + (parseFloat(String(rec.amount ?? 0)) || 0), 0);
});

// Helper: get operador name from usersMap with permissionsService fallback
const getOperadorName = (userId: Id | null | undefined): string => {
  if (userId === null || userId === undefined) return '';
  const userKey = String(userId);
  const mapped = props.usersMap[userKey];
  return mapped?.name ||
         mapped?.user_name ||
         permissionsService.getUserNameBySub(String(userId)) ||
         String(userId);
};

// Helper: format date
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return formatDateLocale(date);
};

// DataTable configs (config-driven mode)
const currentAdvancesTableConfig = computed(() => ({
  tableId: 'payroll-cash-advances-current',
  columns: [
    {
      data: 'recebimento_data',
      title: t('Data'),
      sortable: true,
      cellRenderer: (params: { value?: unknown }) => formatDate(params.value as string | null | undefined)
    },
    { data: 'cliente_nome', title: t('Cliente'), sortable: true },
    {
      data: 'user_id',
      title: t('Operador'),
      sortable: true,
      cellRenderer: (params: { value?: unknown }) => getOperadorName(params.value as Id | null | undefined)
    },
    {
      data: 'amount',
      title: t('Valor'),
      sortable: true,
      renderType: 'currency',
      dataType: 'number'
    },
    {
      data: 'id',
      title: t('Ações'),
      width: 80,
      sortable: false,
      renderType: 'clickable_button',
      buttonClass: 'p-button-danger p-button-text p-button-sm',
      buttonIcon: 'pi pi-trash',
      buttonText: '',
      buttonAction: 'remove'
    }
  ],
  pagination: currentAdvances.value.length > 5,
  pageLength: 5
}));

const availableRecebimentosTableConfig = computed(() => ({
  tableId: 'payroll-cash-recebimentos-available',
  checkbox: { enabled: true, position: 'first' },
  columns: [
    {
      data: 'recebimento_data',
      title: t('Data'),
      sortable: true,
      cellRenderer: (params: { value?: unknown }) => formatDate(params.value as string | null | undefined)
    },
    { data: 'cliente_nome', title: t('Cliente'), sortable: true },
    {
      data: 'operador_user_id',
      title: t('Operador'),
      sortable: true,
      cellRenderer: (params: { value?: unknown }) => getOperadorName(params.value as Id | null | undefined)
    },
    { data: 'forma_pagamento_nome', title: t('Pagamento') },
    {
      data: 'amount',
      title: t('Valor'),
      sortable: true,
      renderType: 'currency',
      dataType: 'number'
    }
  ],
  pagination: filteredAvailableRecebimentos.value.length > 10,
  pageLength: 10,
  rowSelection: 'multiple'
}));

const selectedPreviewTableConfig = computed(() => ({
  tableId: 'payroll-cash-selected-preview',
  columns: [
    {
      data: 'recebimento_data',
      title: t('Data'),
      cellRenderer: (params: { value?: unknown }) => formatDate(params.value as string | null | undefined)
    },
    { data: 'cliente_nome', title: t('Cliente') },
    {
      data: 'operador_user_id',
      title: t('Operador'),
      cellRenderer: (params: { value?: unknown }) => getOperadorName(params.value as Id | null | undefined)
    },
    {
      data: 'amount',
      title: t('Valor'),
      renderType: 'currency',
      dataType: 'number'
    }
  ],
  pagination: selectedRecebimentos.value.length > 5,
  pageLength: 5
}));

const handleCurrentAdvancesButtonClick = (payload: ButtonClickPayload): void => {
  if (payload.action === 'remove' && payload.data?.id !== undefined) {
    handleRemoveAdvance(payload.data.id);
  }
};

// Load current advances
const loadCurrentAdvances = async (): Promise<void> => {
  isLoadingAdvances.value = true;
  try {
    log('CashRecebimentosConciliationWizard', 'loadCurrentAdvances', 'Loading advances', { periodId: props.periodId });

    const data = await listAdvancesByPeriod(props.periodId);
    currentAdvances.value = (data?.advances ?? []) as unknown as CurrentAdvanceRow[];
    summaryByUser.value = (data?.summary_by_user ?? []) as unknown as SummaryByUser[];

    log('CashRecebimentosConciliationWizard', 'loadCurrentAdvances', 'Loaded', { count: currentAdvances.value.length });
  } catch (error) {
    console.error('Error loading advances:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao carregar adiantamentos'),
      duration: 5000
    });
  } finally {
    isLoadingAdvances.value = false;
  }
};

// Load available recebimentos
const loadAvailableRecebimentos = async (): Promise<void> => {
  isLoadingRecebimentos.value = true;
  try {
    log('CashRecebimentosConciliationWizard', 'loadAvailableRecebimentos', 'Loading recebimentos', { periodId: props.periodId });

    const data = await fetchAvailableRecebimentos(props.periodId);
    availableRecebimentos.value = (data ?? []) as unknown as AvailableRecebimentoRow[];

    log('CashRecebimentosConciliationWizard', 'loadAvailableRecebimentos', 'Loaded', { count: availableRecebimentos.value.length });
  } catch (error) {
    console.error('Error loading recebimentos:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao carregar recebimentos disponíveis'),
      duration: 5000
    });
  } finally {
    isLoadingRecebimentos.value = false;
  }
};

// Remove an advance
const handleRemoveAdvance = async (advanceId: Id): Promise<void> => {
  deletingId.value = advanceId;
  try {
    log('CashRecebimentosConciliationWizard', 'handleRemoveAdvance', 'Removing advance', { advanceId });

    const success = await deleteAdvance(advanceId, props.franchiseId);

    if (success) {
      eventBus.emit('globalMessage.success', {
        message: t('Adiantamento removido com sucesso'),
        duration: 3000
      });

      // Reload advances and available recebimentos
      await Promise.all([
        loadCurrentAdvances(),
        loadAvailableRecebimentos()
      ]);
    }
  } catch (error) {
    console.error('Error removing advance:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao remover adiantamento'),
      duration: 5000
    });
  } finally {
    deletingId.value = null;
  }
};

// Handle wizard close
const handleClose = (): void => {
  log('CashRecebimentosConciliationWizard', 'handleClose', 'Closing wizard');
  emit('update:visible', false);
  emit('closed');
  resetWizard();
};

// Handle wizard finish
const handleFinish = async (): Promise<void> => {
  if (selectedRecebimentos.value.length === 0) {
    // No new advances to add, just close
    handleClose();
    emit('completed', { added: 0 });
    eventBus.emit('payroll.advances.updated', { periodId: props.periodId });
    return;
  }

  isProcessing.value = true;
  try {
    log('CashRecebimentosConciliationWizard', 'handleFinish', 'Adding advances', { count: selectedRecebimentos.value.length });

    const recebimentoIds = selectedRecebimentos.value.map(rec => Number(rec.recebimento_id));

    const result = await addAdvancesBatch({
      period_id: props.periodId,
      recebimento_ids: recebimentoIds,
      franchise_id: props.franchiseId
    });

    if (result) {
      eventBus.emit('globalMessage.success', {
        message: t('Adiantamentos adicionados com sucesso'),
        duration: 3000
      });

      emit('completed', { added: selectedRecebimentos.value.length });
      eventBus.emit('payroll.advances.updated', { periodId: props.periodId });
      handleClose();
    }
  } catch (error) {
    console.error('Error adding advances:', error);
    eventBus.emit('globalMessage.error', {
      message: (error as Error)?.message || t('Erro ao adicionar adiantamentos'),
      duration: 5000
    });
  } finally {
    isProcessing.value = false;
  }
};

// Reset wizard state
const resetWizard = (): void => {
  selectedRecebimentos.value = [];
  selectedOperadorFilter.value = null;
};

// Watch for visibility changes
watch(() => props.visible, async (newValue) => {
  if (newValue) {
    log('CashRecebimentosConciliationWizard', 'watch:visible', 'Wizard opened, loading data');
    resetWizard();
    await Promise.all([
      loadCurrentAdvances(),
      loadAvailableRecebimentos()
    ]);
  }
});

// Expose methods
defineExpose({
  loadCurrentAdvances,
  loadAvailableRecebimentos
});
</script>

<style scoped>
.wizard-step-content {
  padding: 1rem 0;
}

.step-header {
  margin-bottom: 1.5rem;
}

.step-header h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
  font-size: 1.25rem;
}

.step-subtitle {
  margin: 0;
  color: var(--text-color-secondary);
  font-size: 0.95rem;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

/* KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.kpi-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.kpi-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.kpi-icon.info {
  background: var(--blue-50);
  color: var(--blue-500);
}

.kpi-icon.success {
  background: var(--green-50);
  color: var(--green-500);
}

.kpi-icon.warning {
  background: var(--orange-50);
  color: var(--orange-500);
}

.kpi-content {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-color);
}

.kpi-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

/* Section styling */
.section-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
}

.current-advances-section {
  margin-top: 1.5rem;
}

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem;
  background: var(--surface-50);
  border-radius: 8px;
  border: 2px dashed var(--surface-200);
}

.empty-state i {
  font-size: 3rem;
  color: var(--text-color-secondary);
  margin-bottom: 1rem;
}

.empty-state h4 {
  margin: 0 0 0.5rem 0;
  color: var(--text-color);
}

.empty-state p {
  margin: 0;
  color: var(--text-color-secondary);
}

/* Filters */
.filters-row {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.filter-dropdown {
  min-width: 200px;
}

/* Selection summary */
.selection-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: var(--surface-50);
  border-radius: 8px;
  margin-top: 1rem;
}

.selection-info {
  font-weight: 500;
  color: var(--text-color-secondary);
}

.selection-info strong {
  color: var(--text-color);
}

.no-selection {
  font-style: italic;
}

/* Final summary */
.final-summary {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  background: var(--surface-card);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
  overflow: hidden;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: var(--surface-50);
  border-bottom: 1px solid var(--surface-border);
}

.summary-header i {
  font-size: 1.25rem;
  color: var(--green-500);
}

.summary-header h5 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.summary-body {
  padding: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid var(--surface-100);
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.total {
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 2px solid var(--surface-200);
  border-bottom: none;
  font-size: 1.1rem;
}

.summary-row .label {
  color: var(--text-color-secondary);
}

.summary-row .value {
  font-weight: 600;
  color: var(--text-color);
}

.summary-row .value.highlight {
  color: var(--orange-600);
}

.summary-row.total .label,
.summary-row.total .value {
  font-weight: 700;
  color: var(--text-color);
}

/* Selected preview */
.selected-preview {
  margin-top: 1rem;
}

/* Info message */
.info-message {
  margin-bottom: 1.5rem;
}

/* Responsive */
@media (max-width: 768px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .filters-row {
    flex-direction: column;
  }

  .filter-dropdown {
    min-width: 100%;
  }
}
</style>
